import React from 'react';
import { Button, Dialog, DialogContent, DialogFooter, Flex, Select, Text, TextField, useToast, TagBadge, badgeVariantToTagColor } from '@chatsdet/ui';
import { useCreateApiRequest, useTestApiUrl } from '../api';
import { useOrgPropertySelection } from '../../../hooks/useOrgPropertySelection';
import { useOrgCredentials } from '../../../hooks/useOrgCredentials';

const HTTP_METHODS = [
  { id: 'GET', label: 'GET' },
  { id: 'POST', label: 'POST' },
  { id: 'PUT', label: 'PUT' },
  { id: 'PATCH', label: 'PATCH' },
  { id: 'DELETE', label: 'DELETE' },
];

type CreateEndpointDialogProps = {
  onClose: () => void;
};

export const CreateEndpointDialog: React.FC<CreateEndpointDialogProps> = ({ onClose }) => {
  const [method, setMethod] = React.useState('GET');
  const [url, setUrl] = React.useState('');
  const [name, setName] = React.useState('');
  const [credentialId, setCredentialId] = React.useState<string | null>(null);
  const { toast } = useToast();

  const createMutation = useCreateApiRequest();
  const testMutation = useTestApiUrl();
  const { propertyId, selectProperty, propertyOptions, propertiesQuery } = useOrgPropertySelection();
  const { credentialOptions } = useOrgCredentials();

  const credentialSelectItems = React.useMemo(
    () => [{ id: '__none__', label: 'No credential' }, ...credentialOptions],
    [credentialOptions]
  );

  const handleTest = React.useCallback(() => {
    if (!url.trim() || !propertyId) return;
    testMutation.mutate(
      {
        url: url.trim(),
        method,
        propertyId,
        credentialId: credentialId === '__none__' ? null : credentialId,
      },
      {
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Test failed');
        },
      }
    );
  }, [url, method, propertyId, credentialId, testMutation, toast]);

  const handleCreate = React.useCallback(() => {
    if (!url.trim() || !propertyId) return;
    createMutation.mutate(
      {
        method,
        url: url.trim(),
        propertyId,
        name: name.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success('Endpoint created');
        },
        onSettled: (_data, error) => {
          if (!error) onClose();
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Create failed');
        },
      }
    );
  }, [url, method, propertyId, name, createMutation, toast, onClose]);

  const testResult = testMutation.data;
  const testOk = testResult && 'ok' in testResult && testResult.ok;
  const testError = testResult && 'ok' in testResult && !testResult.ok
    ? (testResult as { error?: string }).error
    : null;

  return (
    <Dialog title="Add API Endpoint" description="Manually add an endpoint to the API catalog.">
      <DialogContent className="space-y-4">
        <Flex gap="sm" align="end">
          <div className="w-[110px] shrink-0">
            <Select
              label="Method"
              items={HTTP_METHODS}
              selectedKey={method}
              onSelectionChange={(key) => setMethod(key as string)}
            />
          </div>
          <div className="flex-1 min-w-0">
            <TextField
              label="URL"
              aria-label="URL"
              value={url}
              onChange={setUrl}
              placeholder="https://api.example.com/v1/users"
            />
          </div>
        </Flex>

        <TextField
          label="Name (optional)"
          aria-label="Name"
          value={name}
          onChange={setName}
          placeholder="List users"
        />

        <Select
          label="Application"
          items={propertyOptions}
          selectedKey={propertyId}
          onSelectionChange={(key) => selectProperty(key as string)}
          placeholder="Select a application..."
          isDisabled={propertiesQuery.isLoading}
        />

        <Select
          label="Credential (for testing)"
          items={credentialSelectItems}
          selectedKey={credentialId ?? '__none__'}
          onSelectionChange={(key) => setCredentialId(key === '__none__' ? null : (key as string))}
          placeholder="Select a credential..."
        />

        <Flex align="center" gap="sm">
          <Button
            variant="outline"
            size="sm"
            onPress={handleTest}
            isDisabled={!url.trim() || !propertyId || testMutation.isPending}
          >
            {testMutation.isPending ? 'Testing...' : 'Test URL'}
          </Button>
          {testOk && 'data' in testResult! && (
            <Flex align="center" gap="xs">
              <TagBadge name={(testResult as { data: { statusCode: number } }).data.statusCode} color={badgeVariantToTagColor((testResult as { data: { statusCode: number } }).data.statusCode < 400
                                              ? 'success'
                                              : 'destructive')} hideDot size="sm" />
              <Text size="xs" className="text-muted-foreground">
                {(testResult as { data: { durationMs: number } }).data.durationMs}ms
              </Text>
            </Flex>
          )}
          {testError && (
            <Text size="xs" className="text-destructive">
              {testError}
            </Text>
          )}
        </Flex>
      </DialogContent>
      <DialogFooter>
        <Button variant="secondary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onPress={handleCreate}
          isDisabled={!url.trim() || !propertyId || createMutation.isPending}
        >
          {createMutation.isPending ? 'Creating...' : 'Create'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
